import { serializeJsonLd } from "@/lib/safeJsonLd";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * Route → Hebrew label map.
 * Dynamic segments (e.g. :slug) are handled separately.
 */
const ROUTE_LABELS: Record<string, string> = {
 "כניסה": "כניסה",
 "איפוס-סיסמה": "איפוס סיסמה",
 "הפוך-לספק": "הפוך לספק",
 "פורטל-ספקים": "פורטל ספקים",
 "חנות": "חנות",
 "שותפים": "שותפים",
 "הזמנות": "ההזמנות שלי",
 "תשלום": "תשלום",
 "אישור-הזמנה": "אישור הזמנה",
 "פרופיל": "פרופיל",
 "מועדפים": "מועדפים",
 "רשימת-מתנות": "רשימת מתנות",
 "הביקורות-שלי": "הביקורות שלי",
 "קטגוריות": "קטגוריות",
 "קטגוריה": "קטגוריה",
 "מוצרים-חדשים": "מוצרים חדשים",
 "מבצעים": "מבצעים",
 "הנחות-יומיות": "הנחות יומיות",
 "קופונים": "קופונים",
 "מרכז-עזרה": "מרכז עזרה",
 "מדיניות-החזרות": "מדיניות החזרות",
 "משלוחים": "משלוחים",
 "צור-קשר": "צור קשר",
 "הצהרת-נגישות": "הצהרת נגישות",
 "הגדרות": "הגדרות",
 "מדיניות-פרטיות": "מדיניות פרטיות",
 "מידע-משפטי": "מידע משפטי",
 "מוצר": "מוצר",
 "product": "מוצר",
 "אודות": "אודות",
};

/** Pages where breadcrumbs should NOT appear */
const HIDDEN_ROUTES = ["/", "/ניהול"];

const Breadcrumbs = () => {
  const { pathname } = useLocation();

  // Hide on home, admin, and standalone routes
  if (HIDDEN_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"))) {
    return null;
  }

  const segments = decodeURIComponent(pathname).split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const crumbs: { label: string; path: string }[] = [];
  let currentPath = "";

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    currentPath += `/${seg}`;

    const label = ROUTE_LABELS[seg] || seg.replace(/-/g, " ");
    
    // For product pages, skip the "מוצר" prefix segment - just show product name
    const isProductRoute = segments[0] === "מוצר" || segments[0] === "product";
    if (isProductRoute && i === 0) {
      continue;
    } else if (isProductRoute && i === 1) {
      crumbs.push({ label: decodeURIComponent(seg).replace(/-/g, " "), path: currentPath });
    } else if (segments[0] === "קטגוריה" && i === 0) {
      crumbs.push({ label: "קטגוריות", path: "/קטגוריות" });
    } else if (segments[0] === "קטגוריה" && i === 1) {
      const catNames: Record<string, string> = {
        electronics: "אלקטרוניקה", fashion: "אופנה", "home-garden": "בית וגן",
        beauty: "יופי וטיפוח", babies: "תינוקות", sports: "ספורט",
        computers: "מחשבים", watches: "שעונים", shoes: "נעליים",
        stationery: "כלי כתיבה ומשרד",
      };
      crumbs.push({ label: catNames[seg] || seg, path: currentPath });
    } else if (segments[0] === "קטגוריה" && i === 2) {
      crumbs.push({ label: decodeURIComponent(seg), path: currentPath });
    } else {
      crumbs.push({ label, path: currentPath });
    }
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "ראשי", item: `${origin}/` },
      ...crumbs.map((crumb, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: crumb.label,
        ...(i < crumbs.length - 1 ? { item: `${origin}${crumb.path}` } : {}),
      })),
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{serializeJsonLd(breadcrumbJsonLd)}</script>
      </Helmet>
      <nav
        aria-label="Breadcrumb"
        dir="rtl"
        className="bg-muted/30 border-b border-border/50"
      >
        <div className="container py-2">
          <ol className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground flex-wrap">
            <li>
              <Link
                to="/"
                className="flex items-center gap-1 hover:text-primary transition-colors"
              >
                <Home size={14} />
                <span className="hidden sm:inline">ראשי</span>
              </Link>
            </li>
            {crumbs.map((crumb, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <li key={crumb.path} className="flex items-center gap-1">
                  <ChevronLeft size={12} className="text-muted-foreground/50" />
                  {isLast ? (
                    <span className="font-medium text-foreground truncate max-w-[200px] sm:max-w-[300px]">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.path}
                      className="hover:text-primary transition-colors truncate max-w-[150px]"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
